import React, { useState } from 'react';
import { arrayOf, bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import config from '../../config';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  NamedLink,
  SectionCustomerLogos,
  JoinPreFooter,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { HiSparkles } from 'react-icons/hi2';
import { BsFillCheckCircleFill } from 'react-icons/bs';
import { GrRadial, GrRadialSelected } from 'react-icons/gr';
import { MdVerified } from 'react-icons/md';
import { IoShieldCheckmark } from 'react-icons/io5';

import { loadData } from './AiLandingPage.duck';

import facebookImage from '../../assets/freedomlySocialShare.png';
import twitterImage from '../../assets/freedomlySocialShare.png';
import heroImage1 from '../../assets/ai-page-hero-1.png';
import heroImage2 from '../../assets/ai-page-hero-2.png';
import heroImage3 from '../../assets/ai-page-hero-3.png';

import css from './AiLandingPage.css';

const HERO_CUSTOMER_LOGOS = [
  'hartwall',
  'invesdor',
  'peikko',
  'haltian',
  'eerikkila',
  'helsinki',
  'corego',
  'zervant',
];

const OPTION_KNOW = 'know';
const OPTION_UNSURE = 'unsure';

const SECONDS_PER_CAROUSEL_CARD = 6;

const EXAMPLE_CARDS = [1, 2, 3].map(cardIndex => ({
  titleId: `AiLandingPage.exampleCardTitle${cardIndex}`,
  descriptionId: `AiLandingPage.exampleCardDescription${cardIndex}`,
  itemIds: [1, 2, 3].map(itemIndex => `AiLandingPage.exampleCard${cardIndex}Item${itemIndex}`),
}));

const EXAMPLE_CHIP_IDS = [1, 2, 3, 4, 5, 6, 7].map(i => `AiLandingPage.exampleChip${i}`);

const KNOW_STEPS = [1, 2, 3].map(stepIndex => ({
  titleId: `AiLandingPage.knowStep${stepIndex}Title`,
  textId: `AiLandingPage.knowStep${stepIndex}Text`,
  pillId: `AiLandingPage.knowStep${stepIndex}Pill`,
}));

// The last step is shared between the two states in the design.
const UNSURE_STEPS = [1, 2]
  .map(stepIndex => ({
    titleId: `AiLandingPage.unsureStep${stepIndex}Title`,
    textId: `AiLandingPage.unsureStep${stepIndex}Text`,
    pillId: `AiLandingPage.unsureStep${stepIndex}Pill`,
  }))
  .concat([KNOW_STEPS[2]]);

// Page-local variant of VerifiedUserMark: the shared component's tooltip and
// badge positioning don't play well inside the transform-animated carousel
// track, so the carousel renders the plain badge icons itself.
const CarouselVerifiedMark = ({ user }) => {
  const metadata = user?.attributes?.profile?.metadata;

  if (metadata?.partOfFreedomly) {
    return (
      <span className={css.partOfFreedomlyMark}>
        <IoShieldCheckmark />
      </span>
    );
  } else if (metadata?.verified) {
    return (
      <span className={css.verifiedMark}>
        <MdVerified className={css.verifiedMarkIcon} />
      </span>
    );
  }
  return null;
};

const FreelancerCard = ({ user }) => {
  const profile = user?.attributes?.profile;
  const variants = user?.profileImage?.attributes?.variants;
  const imageUrl = variants?.['square-small2x']?.url || variants?.['square-small']?.url;

  return (
    <div className={css.freelancerCard}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={profile?.firstName || ''}
          className={css.freelancerCardImage}
          loading="lazy"
        />
      ) : null}
      <div className={css.freelancerCardInfo}>
        <div className={css.freelancerCardNameRow}>
          <span className={css.freelancerCardName}>{profile?.firstName}</span>
          <CarouselVerifiedMark user={user} />
        </div>
        <div className={css.freelancerCardWhoAmI}>{profile?.publicData?.whoAmI}</div>
      </div>
    </div>
  );
};

const StepCard = ({ titleId, textId, pillId }) => (
  <div className={css.stepCard}>
    <div>
      <h3 className={css.stepCardTitle}>
        <FormattedMessage id={titleId} />
      </h3>
      <p className={css.stepCardText}>
        <FormattedMessage id={textId} />
      </p>
    </div>
    <div className={css.stepCardPill}>
      <BsFillCheckCircleFill className={css.checkIcon} />
      <FormattedMessage id={pillId} />
    </div>
  </div>
);

export const AiLandingPageComponent = props => {
  const { intl, scrollingDisabled, usersAtAiPage } = props;

  const [selectedOption, setSelectedOption] = useState(OPTION_KNOW);

  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'AiLandingPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'AiLandingPage.schemaDescription' });
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  const handleShowSpaceHome = () => {
    if (typeof window === 'object') {
      window.Intercom('showSpace', 'home');
    }
  };

  const heroTitleHighlight = (
    <span className={css.heroTitleHighlight}>
      <FormattedMessage id="AiLandingPage.heroTitleHighlight" />
    </span>
  );

  // The list is rendered twice so the marquee animation can loop seamlessly.
  const carouselUsers = usersAtAiPage.concat(usersAtAiPage);
  const carouselDuration = `${usersAtAiPage.length * SECONDS_PER_CAROUSEL_CARD}s`;

  const steps = selectedOption === OPTION_KNOW ? KNOW_STEPS : UNSURE_STEPS;

  const selectionOption = (option, labelId) => {
    const isSelected = selectedOption === option;
    const RadioIcon = isSelected ? GrRadialSelected : GrRadial;
    return (
      <button
        type="button"
        className={isSelected ? css.selectionOptionSelected : css.selectionOption}
        onClick={() => setSelectedOption(option)}
      >
        <RadioIcon className={css.selectionOptionRadio} />
        <span className={css.selectionOptionLabel}>
          <FormattedMessage id={labelId} />
        </span>
      </button>
    );
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[
        { url: `${config.canonicalRootURL}${facebookImage}`, width: 1200, height: 630 },
      ]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 600, height: 314 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="AiLandingPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.sections}>
            <section className={css.heroSection}>
              <div className={css.heroContent}>
                <div className={css.heroTextColumn}>
                  <h1 className={css.heroTitle}>
                    <FormattedMessage
                      id="AiLandingPage.heroTitle"
                      values={{ highlight: heroTitleHighlight }}
                    />
                  </h1>
                  <p className={css.heroSubtitle}>
                    <FormattedMessage id="AiLandingPage.heroSubtitle" />
                  </p>
                  <NamedLink name="SignupPage" className={css.heroButton}>
                    <FormattedMessage id="AiLandingPage.heroButton" />
                  </NamedLink>
                </div>
                <div className={css.heroImageColumn}>
                  <div className={css.heroImages}>
                    <img src={heroImage1} alt="" className={css.heroImage1} />
                    <img src={heroImage2} alt="" className={css.heroImage2} />
                    <img src={heroImage3} alt="" className={css.heroImage3} />
                    <div className={css.heroSparkleBadge}>
                      <HiSparkles className={css.heroSparkleIcon} />
                    </div>
                  </div>
                  <div className={css.heroNetworkBadge}>
                    <BsFillCheckCircleFill className={css.checkIcon} />
                    <FormattedMessage id="AiLandingPage.heroBadge" />
                  </div>
                </div>
              </div>
              <div className={css.heroLogos}>
                <SectionCustomerLogos toShow={HERO_CUSTOMER_LOGOS} />
              </div>
            </section>

            <section className={css.examplesSection}>
              <h2 className={css.examplesTitle}>
                <FormattedMessage id="AiLandingPage.examplesTitle" />
              </h2>
              <div className={css.exampleCards}>
                {EXAMPLE_CARDS.map(card => (
                  <div key={card.titleId} className={css.exampleCard}>
                    <h3 className={css.exampleCardTitle}>
                      <FormattedMessage id={card.titleId} />
                    </h3>
                    <p className={css.exampleCardDescription}>
                      <FormattedMessage id={card.descriptionId} />
                    </p>
                    <div className={css.exampleCardItems}>
                      <div className={css.exampleCardItemsLabel}>
                        <FormattedMessage id="AiLandingPage.examplesLabel" />
                      </div>
                      {card.itemIds.map(itemId => (
                        <div key={itemId} className={css.exampleCardItem}>
                          <BsFillCheckCircleFill className={css.checkIcon} />
                          <FormattedMessage id={itemId} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className={css.examplesMore}>
                <div className={css.examplesMoreTitle}>
                  <FormattedMessage id="AiLandingPage.examplesMoreTitle" />
                </div>
                <div className={css.examplesChips}>
                  {EXAMPLE_CHIP_IDS.map(chipId => (
                    <div key={chipId} className={css.examplesChip}>
                      <FormattedMessage id={chipId} />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {usersAtAiPage.length > 0 ? (
              <section className={css.carouselSection}>
                <div className={css.carouselCaption}>
                  <FormattedMessage id="AiLandingPage.carouselCaption" />
                </div>
                <h2 className={css.carouselTitle}>
                  <FormattedMessage id="AiLandingPage.carouselTitle" />
                </h2>
                <div className={css.carouselContainer}>
                  <div
                    className={css.carouselTrack}
                    style={{ animationDuration: carouselDuration }}
                  >
                    {carouselUsers.map((user, index) => (
                      <FreelancerCard key={`${user.id?.uuid}-${index}`} user={user} />
                    ))}
                  </div>
                </div>
              </section>
            ) : null}

            <section className={css.selectionSection}>
              <h2 className={css.selectionTitle}>
                <FormattedMessage id="AiLandingPage.selectionTitle" />
              </h2>
              <div className={css.selectionQuestion}>
                <FormattedMessage id="AiLandingPage.selectionQuestion" />
              </div>
              <div className={css.selectionOptions}>
                {selectionOption(OPTION_KNOW, 'AiLandingPage.selectionOptionKnow')}
                {selectionOption(OPTION_UNSURE, 'AiLandingPage.selectionOptionUnsure')}
              </div>
              <div className={css.selectionStepsLabel}>
                <FormattedMessage id="AiLandingPage.selectionStepsLabel" />
              </div>
              <div className={css.stepCards}>
                {steps.map(step => (
                  <StepCard key={step.titleId} {...step} />
                ))}
              </div>
              <div className={css.selectionButtons}>
                {selectedOption === OPTION_KNOW ? (
                  <NamedLink name="SignupPage" className={css.selectionPrimaryButton}>
                    <FormattedMessage id="AiLandingPage.selectionPrimaryButton" />
                  </NamedLink>
                ) : null}
                <button
                  type="button"
                  className={css.selectionSecondaryButton}
                  onClick={handleShowSpaceHome}
                >
                  <FormattedMessage id="AiLandingPage.selectionSecondaryButton" />
                </button>
              </div>
            </section>
          </div>
          <JoinPreFooter />
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

AiLandingPageComponent.defaultProps = {
  usersAtAiPage: [],
};

AiLandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  usersAtAiPage: arrayOf(object),

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { usersAtAiPage } = state.AiLandingPage;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    usersAtAiPage,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AiLandingPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(AiLandingPageComponent);

AiLandingPage.loadData = loadData;

export default AiLandingPage;
